<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { io } from 'socket.io-client'
import { useResultsStore } from '../stores/results-fetcher.js'
import { useStateStore } from '../stores/state.js'
import { useRoute } from 'vue-router'
import EventSwitcher from './EventSwitcher.vue'
import { WS_URL } from '../config.js'

const WEBSOCKET_URL = WS_URL

const resultsStore = useResultsStore()
const stateStore = useStateStore()
const route = useRoute()
const eventAlias = route.params.eventAlias

const feed = ref([])
const connected = ref(false)
let socket = null
let joinedEventId = null

function buildFeedItem(runner) {
  const finishTime = runner.finish_time || runner.finish_time_gps
  return {
    key: `${runner.id}-${finishTime}`,
    number: runner.number,
    name: runner.name,
    bio: runner.bio,
    time: runner.timeScore,
    categoryColor: resultsStore.getCategoryColor(runner.category_id) || '#6c757d',
    startTime: runner.start_time,
    finishTime,
    isGps: !runner.finish_time && !!runner.finish_time_gps
  }
}

const stats = computed(() => {
  const finished = resultsStore.results.filter(r => r.finish_time || r.finish_time_gps)
  const byCategory = new Map()
  finished.forEach(runner => {
    const id = runner.category_id
    if (!byCategory.has(id)) {
      const category = resultsStore.categories.find(cat => cat.id == id)
      byCategory.set(id, {
        name: category?.name || 'Bez trasy',
        color: resultsStore.getCategoryColor(id) || '#6c757d',
        count: 0
      })
    }
    byCategory.get(id).count += 1
  })
  return {
    total: finished.length,
    categories: Array.from(byCategory.values()).sort((a, b) => a.name.localeCompare(b.name))
  }
})

function buildInitialFeed() {
  feed.value = resultsStore.results
    .filter(r => r.finish_time || r.finish_time_gps)
    .map(buildFeedItem)
    .sort((a, b) => new Date(b.finishTime) - new Date(a.finishTime))
}

function handleFinishPunch(data) {
  if (data.mode !== 'finish') {
    return
  }
  const punchTime = data.punch_timestamp || data.timestamp
  const runner = resultsStore.applyFinishPunch(data.runner_id, punchTime)
  if (!runner) {
    return
  }
  feed.value.unshift(buildFeedItem(runner))
}

function connectSocket(eventId) {
  if (joinedEventId === eventId) {
    return
  }
  joinedEventId = eventId
  if (!socket) {
    socket = io(WEBSOCKET_URL)
    socket.on('connect', () => {
      connected.value = true
      if (joinedEventId != null) {
        socket.emit('join_event', joinedEventId)
      }
    })
    socket.on('disconnect', () => {
      connected.value = false
    })
    socket.on('connect_error', (err) => {
      console.error('Websocket connect_error:', err.message)
    })
    socket.on('new_punch', handleFinishPunch)
  } else if (connected.value) {
    socket.emit('join_event', eventId)
  }
}

watch(
  () => resultsStore.event,
  (event) => {
    if (event && event.event?.id != null) {
      connectSocket(event.event.id)
    }
  },
  { immediate: true }
)

resultsStore.fetchResults(eventAlias)
resultsStore.fetchEventDetails(eventAlias)
stateStore.fetchEvents()

watch(
  () => resultsStore.results,
  () => {
    if (feed.value.length === 0) {
      buildInitialFeed()
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  if (socket) {
    socket.disconnect()
    socket = null
  }
})
</script>

<template>
  <div>
    <EventSwitcher />
    <h1>Potrójna Korona - Meta</h1>
    <div class="settings">
      <span :class="['badge', connected ? 'bg-success' : 'bg-danger']">
        {{ connected ? 'Połączono' : 'Brak połączenia' }}
      </span>
    </div>

    <div class="stats-bar mb-2">
      <span class="badge bg-secondary">Razem: {{ stats.total }}</span>
      <span
        v-for="cat in stats.categories"
        :key="cat.name"
        class="badge stats-category-badge"
        :style="{ backgroundColor: cat.color }"
      >{{ cat.name }}: {{ cat.count }}</span>
    </div>

    <div class="finish-feed">
      <div v-if="feed.length === 0" class="text-muted">Brak wyników z mety.</div>
      <div v-for="item in feed" :key="item.key" class="finish-item">
        <div class="runner-info">
          <span class="number-badge" :style="{ backgroundColor: item.categoryColor }">{{ item.number }}</span>
          <div class="name-block">
            <span class="name">{{ item.name }}<span v-if="item.bio"> ({{ item.bio }})</span></span>
            <div class="times">
              <span>Start: {{ item.startTime ? new Date(item.startTime).toLocaleTimeString() : '--:--:--' }}</span>
              <span>Meta: {{ item.finishTime ? new Date(item.finishTime).toLocaleTimeString() : '--:--:--' }}
                <span v-if="item.isGps" class="badge bg-warning text-dark gps-badge">GPS</span>
              </span>
            </div>
          </div>
        </div>
        <div class="result-block">
          <span class="result-time">{{ item.time }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.settings {
  position: fixed;
  top: 0.6rem;
  right: 0.6rem;
  z-index: 10;
}

.finish-feed {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  max-height: 80vh;
  overflow-y: auto;
  width: 100%;
}

/* On wide screens the feed shouldn't stretch edge-to-edge - keep it a
   comfortable card-list width, centered, like on mobile. */
@media (min-width: 768px) {
  .finish-feed,
  .stats-bar {
    max-width: 640px;
    margin: 0 auto;
  }
}

.finish-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  padding: 0.6rem 0.9rem;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  background: #fff;
  animation: highlight-row 1.5s ease-out;
}

.runner-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex: 1 1 220px;
}

.number-badge {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  background: #495057;
  color: #fff;
  font-size: 1.15rem;
  font-weight: 700;
}

.name-block {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.times {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  font-size: 0.8rem;
  color: #6c757d;
}

.gps-badge {
  margin-left: 4px;
  font-size: 0.65rem;
  vertical-align: middle;
}

.result-block {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 0 0 auto;
}

.result-time {
  font-size: 1.4rem;
  font-weight: 700;
}

.stats-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.stats-category-badge {
  color: #fff;
  font-size: 0.85rem;
}

@keyframes highlight-row {
  0% { background: #d4edda; }
  100% { background: #fff; }
}
</style>
